import { useVfxShot, useAssignVfxShots, useVfxShots, useUpdateVfxShot } from '@/hooks/useVfxShots'
import { useClips } from '@/hooks/useClips'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useProject } from '@/hooks/useProjects'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { VfxShotThumbnailUpload } from './VfxShotThumbnailUpload'
import { VfxShotRelationships } from './VfxShotRelationships'
import { VfxShotTagsSection } from './VfxShotTagsSection'
import { AssigneeDisplay } from '@/components/shared/AssigneeDisplay'
import { AssigneeSelector } from '@/components/shared/AssigneeSelector'
import { CommentsList } from '@/components/comments/CommentsList'
import { toast } from 'sonner'
import {
  Clock,
  UserCircle,
  Calendar,
  Film,
  Layers,
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowRight,
} from 'lucide-react'
import type { VfxShotStatus } from '@/types/database'

interface VfxShotDetailContentProps {
  shotId: string
  projectId: string
}

const getStatusIcon = (status: VfxShotStatus) => {
  switch (status) {
    case 'yellow':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />
    case 'green':
      return <CheckCircle className="h-4 w-4 text-green-500" />
    case 'in_progress':
      return <Clock className="h-4 w-4 text-blue-500" />
    case 'review_ready':
      return <ArrowRight className="h-4 w-4 text-purple-500" />
    case 'approved':
      return <CheckCircle className="h-4 w-4 text-emerald-500" />
    case 'omit':
      return <XCircle className="h-4 w-4 text-red-500" />
    case 'back_in':
      return <ArrowRight className="h-4 w-4 text-cyan-500" />
    default:
      return <AlertCircle className="h-4 w-4" />
  }
}

// Status helper functions unused for now
// const getStatusLabel = (status: VfxShotStatus) => {
//   switch (status) {
//     case 'yellow': return 'Yellow'
//     case 'green': return 'Green'
//     case 'in_progress': return 'In Progress'
//     case 'review_ready': return 'Review Ready'
//     case 'approved': return 'Approved'
//     case 'omit': return 'Omit'
//     case 'back_in': return 'Back In'
//     default: return status
//   }
// }

// const getStatusBadgeVariant = (status: VfxShotStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
//   switch (status) {
//     case 'yellow': return 'secondary'
//     case 'green': return 'default'
//     case 'approved': return 'default'
//     case 'omit': return 'destructive'
//     default: return 'outline'
//   }
// }

const formatDueDate = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`
  if (diffDays === 0) return 'Due today'
  if (diffDays === 1) return 'Due tomorrow'
  return `Due in ${diffDays} days`
}

export function VfxShotDetailContent({ shotId, projectId }: VfxShotDetailContentProps) {
  const { data: shot, isLoading: shotLoading, refetch: refetchShot } = useVfxShot(shotId)
  const { data: clips = [] } = useClips(projectId)
  useVfxShots(projectId) // Keep hook call for future use
  const { data: project } = useProject(projectId)
  const { flags } = useFeatureFlags(projectId)
  const permissions = usePermissions(projectId, project?.owner_user_id)
  const assignVfxShots = useAssignVfxShots()
  const updateVfxShot = useUpdateVfxShot()

  // const _vfxShots = vfxShotsData?.shots || [] // Available for future use

  const handleRelationshipUpdate = () => {
    refetchShot()
  }

  const handleStatusChange = async (newStatus: VfxShotStatus) => {
    try {
      await updateVfxShot.mutateAsync({
        shotId,
        updates: {
          status: newStatus,
        },
      })
      toast.success('Status updated successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update status')
    }
  }

  const handleAssignmentChange = async (userId: string | null) => {
    if (!userId) {
      toast.error('Please select a team member to assign')
      return
    }

    try {
      await assignVfxShots.mutateAsync({
        projectId,
        assignRequest: {
          shot_ids: [shotId],
          assigned_user_id: userId,
        },
      })
      toast.success('VFX shot assigned successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update assignment')
    }
  }

  if (shotLoading) {
    return <div className="text-sm text-muted-foreground">Loading shot details...</div>
  }

  if (!shot) {
    return <div className="text-sm text-destructive">Shot not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Shot Name and Thumbnail */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            {flags.enable_vfx_shot_thumbnails && (
              <div className="flex-shrink-0">
                <VfxShotThumbnailUpload vfxShot={shot} projectId={projectId} />
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">{shot.shot_name}</h3>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tags Section */}
      <VfxShotTagsSection vfxShot={shot} projectId={projectId} canEditTags={permissions.canEditTags} />

      {/* Status Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            {getStatusIcon(shot.status)}
            Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={shot.status}
            onValueChange={handleStatusChange}
            disabled={updateVfxShot.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yellow">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <span>Yellow - Pending</span>
                </div>
              </SelectItem>
              <SelectItem value="green">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Green - Approved for work</span>
                </div>
              </SelectItem>
              <SelectItem value="in_progress">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span>In Progress - Artist working</span>
                </div>
              </SelectItem>
              <SelectItem value="review_ready">
                <div className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-purple-500" />
                  <span>Review Ready - Submitted</span>
                </div>
              </SelectItem>
              <SelectItem value="approved">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>Approved - Final approval</span>
                </div>
              </SelectItem>
              <SelectItem value="omit">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span>Omit - Shot removed</span>
                </div>
              </SelectItem>
              <SelectItem value="back_in">
                <div className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-cyan-500" />
                  <span>Back In - Brought back</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Assignment Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <UserCircle className="h-4 w-4" />
            Assignment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Assigned To</Label>
            {permissions.canAssignWork ? (
              <AssigneeSelector
                projectId={projectId}
                value={shot.assigned_user_id}
                onValueChange={handleAssignmentChange}
                disabled={assignVfxShots.isPending}
              />
            ) : (
              <div className="text-sm">
                {shot.assigned_user_id ? (
                  <span>Assigned</span>
                ) : (
                  <span className="text-muted-foreground">Not assigned</span>
                )}
              </div>
            )}
          </div>

          {shot.supervisor_user_id && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Supervisor</Label>
              <AssigneeDisplay
                projectId={projectId}
                userId={shot.supervisor_user_id}
                showUnassigned={false}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metadata Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {/* Frame Range */}
            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                <Film className="h-3 w-3" />
                Start Frame
              </p>
              <p className="font-medium font-mono break-words bg-muted/30 p-2 rounded">{shot.frame_range_start}</p>
            </div>

            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                <Film className="h-3 w-3" />
                End Frame
              </p>
              <p className="font-medium font-mono break-words bg-muted/30 p-2 rounded">{shot.frame_range_end}</p>
            </div>

            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                <Film className="h-3 w-3" />
                Duration
              </p>
              <p className="font-medium font-mono break-words bg-muted/30 p-2 rounded">{shot.frame_duration} frames</p>
            </div>

            {/* Priority */}
            <div className="space-y-1">
              <p className="text-muted-foreground font-medium uppercase">Priority</p>
              <p className="font-medium break-words bg-muted/30 p-2 rounded">{shot.priority || 'Normal'}</p>
            </div>

            {/* Due Date */}
            {shot.due_date && (
              <>
                <div className="space-y-1">
                  <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Due Date
                  </p>
                  <p className="font-medium break-words bg-muted/30 p-2 rounded">
                    {new Date(shot.due_date).toLocaleDateString()}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-muted-foreground font-medium uppercase">Status</p>
                  <p className="font-medium break-words bg-muted/30 p-2 rounded">{formatDueDate(shot.due_date)}</p>
                </div>
              </>
            )}

            {/* Description */}
            {shot.custom_metadata?.description && (
              <div className="space-y-1 col-span-2">
                <p className="text-muted-foreground font-medium uppercase">Description</p>
                <p className="font-medium break-words bg-muted/30 p-2 rounded">{shot.custom_metadata.description}</p>
              </div>
            )}

            {/* Notes */}
            {shot.notes && (
              <div className="space-y-1 col-span-2">
                <p className="text-muted-foreground font-medium uppercase">Notes</p>
                <p className="font-medium whitespace-pre-wrap break-words bg-muted/30 p-2 rounded">{shot.notes}</p>
              </div>
            )}

            {/* Custom Metadata */}
            {shot.custom_metadata && Object.entries(shot.custom_metadata)
              .filter(([key]) => key !== 'description')
              .map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <p className="text-muted-foreground font-medium uppercase">{key}</p>
                  <p className="font-medium break-words bg-muted/30 p-2 rounded">
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  </p>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Relationships Section (Linked Clips) */}
      <VfxShotRelationships
        vfxShot={shot}
        availableClips={clips}
        onUpdate={handleRelationshipUpdate}
        canLink={permissions.canLinkVfxShots}
      />

      {/* Comments */}
      <CommentsList
        projectId={projectId}
        commentableType="shot"
        commentableId={shotId}
      />

      {/* Timestamps */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Timestamps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Created</Label>
            <p className="text-xs">{new Date(shot.created_at).toLocaleString()}</p>
          </div>
          {shot.updated_at && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Updated</Label>
              <p className="text-xs">{new Date(shot.updated_at).toLocaleString()}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
